import { useState, useEffect, useRef } from 'react';
import API from '../api/client';

/**
 * Champ « Nom du médicament » avec autocomplétion sur le référentiel national.
 * La saisie libre reste possible (le médecin peut taper un produit absent de la base).
 *
 * Props:
 *  - value          : texte du nom (contrôlé)
 *  - onChange(nom)  : saisie libre
 *  - onPick(med)    : appelé quand une suggestion est choisie (pour remplir dosage, etc.)
 *  - placeholder
 *  - className      : classes du conteneur (ex: col-span-2)
 *  - inputClassName : classes de l'input (pour coller au style de la page)
 */
export default function MedicationAutocomplete({
  value,
  onChange,
  onPick,
  placeholder = 'Nom du médicament',
  className = '',
  inputClassName = 'w-full px-3 py-1.5 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:border-emerald-400',
}) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const boxRef = useRef(null);
  const skipNext = useRef(false); // ne pas relancer une recherche juste après une sélection

  useEffect(() => {
    const term = (value || '').trim();
    if (skipNext.current) { skipNext.current = false; return; }
    if (!focused || term.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await API.get('/medications', { params: { search: term, limit: 15 } });
        setResults(r.data);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [value, focused]);

  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pick = (m) => {
    skipNext.current = true;
    onChange(m.nom);
    onPick?.(m);
    setResults([]);
    setOpen(false);
  };

  const dosageLabel = (m) => [m.dosage, m.unite_dosage].filter(Boolean).join(' ');
  const term = (value || '').trim();

  // Couleur du badge de statut de commercialisation.
  const statutStyle = (s) => {
    if (!s) return 'bg-stone-100 text-stone-500';
    if (s.startsWith('Commercialisé')) return 'bg-emerald-100 text-emerald-700';
    if (/Retiré|Suspendu/i.test(s)) return 'bg-red-100 text-red-700';
    return 'bg-amber-100 text-amber-700'; // Non Commercialisé / AMM sans prix
  };
  const isAvailable = (s) => s && s.startsWith('Commercialisé');

  return (
    <div className={`relative ${className}`} ref={boxRef}>
      <input
        value={value || ''}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={() => setFocused(true)}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        className={inputClassName}
      />
      {open && term.length >= 2 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-stone-200 rounded-md shadow-lg max-h-56 overflow-auto">
          {loading ? (
            <div className="px-3 py-2 text-xs text-stone-400">Recherche…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-stone-400">Aucun médicament — saisie libre possible</div>
          ) : (
            results.map((m) => (
              <button
                type="button"
                key={m.id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(m)}
                className="w-full text-left px-3 py-2 text-xs hover:bg-emerald-50 border-b border-stone-50 last:border-0"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={`font-medium ${isAvailable(m.statut_commercialisation) ? 'text-stone-800' : 'text-stone-500'}`}>
                    {m.nom}
                    {dosageLabel(m) && <span className="text-stone-500"> — {dosageLabel(m)}</span>}
                  </span>
                  {m.statut_commercialisation && (
                    <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${statutStyle(m.statut_commercialisation)}`}>
                      {m.statut_commercialisation}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-stone-400">
                  {[m.forme, m.dci].filter(Boolean).join(' · ')}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
